// PRODUCTION — form step builder types and converters
import { CollectDataFormItemType } from 'src/constants/collect-data/enums'
import type { FormItemOption, TemplateFormItem } from 'src/interfaces/onboardingTemplate'
import { openLinksInNewTab } from 'src/utils/richTextHtml'
import { v4 as uuidv4 } from 'uuid'

export type LocalFormItem = {
  id: string
  type: CollectDataFormItemType
  title: string
  content: string
  url: string
  options: FormItemOption[]
  required: boolean
  checkAnswers: boolean
}

export function newFormItemOption(title: string): FormItemOption {
  return { id: uuidv4(), title, isCorrect: false }
}

export function toLocalItems(items: TemplateFormItem[] | undefined): LocalFormItem[] {
  if (!items) {
    return []
  }

  return items.map((item) => {
    const base: LocalFormItem = {
      id: item.id,
      type: item.type,
      title: '',
      content: '',
      url: '',
      options: [],
      required: false,
      checkAnswers: false,
    }

    switch (item.type) {
      case CollectDataFormItemType.TextBlock:
        return { ...base, content: item.content }
      case CollectDataFormItemType.Video:
        return { ...base, title: item.title, url: item.url }
      case CollectDataFormItemType.TextInput:
      case CollectDataFormItemType.Date:
      case CollectDataFormItemType.Number:
      case CollectDataFormItemType.File:
        return { ...base, title: item.title, required: item.required }
      case CollectDataFormItemType.Select:
      case CollectDataFormItemType.Radio:
      case CollectDataFormItemType.Checkbox:
        return {
          ...base,
          title: item.title,
          required: item.required,
          checkAnswers: item.checkAnswers,
          options: item.options.map((o) => ({
            id: o.id,
            title: o.title,
            ...(o.isCorrect ? { isCorrect: true } : {}),
          })),
        }
      default:
        return base
    }
  })
}

export function toTemplateItems(items: LocalFormItem[]): TemplateFormItem[] {
  const result: TemplateFormItem[] = []

  for (const item of items) {
    switch (item.type) {
      case CollectDataFormItemType.TextBlock:
        // Contractors must not lose the onboarding tab when they follow a link in the text.
        result.push({ id: item.id, type: item.type, content: openLinksInNewTab(item.content) })
        break
      case CollectDataFormItemType.Video:
        result.push({ id: item.id, type: item.type, title: item.title, url: item.url })
        break
      case CollectDataFormItemType.TextInput:
      case CollectDataFormItemType.Date:
      case CollectDataFormItemType.Number:
      case CollectDataFormItemType.File:
        result.push({ id: item.id, type: item.type, title: item.title, required: item.required })
        break
      case CollectDataFormItemType.Select:
      case CollectDataFormItemType.Radio:
      case CollectDataFormItemType.Checkbox: {
        const opts: FormItemOption[] = item.options.map((o, i) => {
          const option: FormItemOption = {
            id: o.id || String(i),
            title: o.title.trim(),
          }

          if (item.checkAnswers && o.isCorrect) {
            option.isCorrect = true
          }

          return option
        })

        result.push({
          id: item.id,
          type: item.type,
          title: item.title,
          required: item.required,
          checkAnswers: item.checkAnswers,
          options: opts,
        })
        break
      }
    }
  }

  return result
}
